import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { acme } from "../../theme/acmeTheme";

const WAVE_PALETTE = [
  {
    offset: 0,
    amplitude: 56,
    frequency: 0.003,
    color: "rgba(34, 197, 94, 0.55)",
    opacity: 0.42,
  },
  {
    offset: Math.PI / 2,
    amplitude: 72,
    frequency: 0.0026,
    color: "rgba(22, 163, 74, 0.45)",
    opacity: 0.32,
  },
  {
    offset: Math.PI,
    amplitude: 48,
    frequency: 0.0034,
    color: "rgba(113, 113, 122, 0.35)",
    opacity: 0.28,
  },
  {
    offset: Math.PI * 1.5,
    amplitude: 64,
    frequency: 0.0022,
    color: "rgba(10, 10, 10, 0.18)",
    opacity: 0.22,
  },
  {
    offset: Math.PI * 2,
    amplitude: 44,
    frequency: 0.004,
    color: "rgba(34, 197, 94, 0.35)",
    opacity: 0.18,
  },
];

/**
 * Animated glow wave lines (inspired by Tanplates/glowy-waves-hero).
 */
export default function GlowyWavesBackground() {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: 0, y: 0 });
  const targetMouseRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let animationId;
    let time = 0;

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const mouseInfluence = prefersReducedMotion ? 8 : 55;
    const influenceRadius = prefersReducedMotion ? 140 : 280;
    const smoothing = prefersReducedMotion ? 0.04 : 0.09;

    const resizeCanvas = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      canvas.width = parent.clientWidth;
      canvas.height = parent.clientHeight;
    };

    const recenterMouse = () => {
      const center = { x: canvas.width / 2, y: canvas.height / 2 };
      mouseRef.current = center;
      targetMouseRef.current = center;
    };

    const handleResize = () => {
      resizeCanvas();
      recenterMouse();
    };

    const handleMouseMove = (event) => {
      const rect = canvas.getBoundingClientRect();
      const inView =
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom &&
        event.clientX >= rect.left &&
        event.clientX <= rect.right;
      if (!inView) {
        recenterMouse();
        return;
      }
      targetMouseRef.current = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
    };

    resizeCanvas();
    recenterMouse();

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);

    const drawWave = (wave) => {
      ctx.save();
      ctx.beginPath();

      for (let x = 0; x <= canvas.width; x += 4) {
        const dx = x - mouseRef.current.x;
        const dy = canvas.height / 2 - mouseRef.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const influence = Math.max(0, 1 - distance / influenceRadius);
        const mouseEffect =
          influence *
          mouseInfluence *
          Math.sin(time * 0.001 + x * 0.01 + wave.offset);

        const y =
          canvas.height * 0.58 +
          Math.sin(x * wave.frequency + time * 0.002 + wave.offset) *
            wave.amplitude +
          Math.sin(x * wave.frequency * 0.4 + time * 0.003) *
            (wave.amplitude * 0.45) +
          mouseEffect;

        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }

      ctx.lineWidth = 2;
      ctx.strokeStyle = wave.color;
      ctx.globalAlpha = wave.opacity;
      ctx.shadowBlur = 28;
      ctx.shadowColor = wave.color;
      ctx.stroke();
      ctx.restore();
    };

    const animate = () => {
      time += prefersReducedMotion ? 0.35 : 1;

      mouseRef.current.x +=
        (targetMouseRef.current.x - mouseRef.current.x) * smoothing;
      mouseRef.current.y +=
        (targetMouseRef.current.y - mouseRef.current.y) * smoothing;

      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, acme.colors.pageBg);
      gradient.addColorStop(1, acme.colors.muted);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
      WAVE_PALETTE.forEach(drawWave);

      animationId = window.requestAnimationFrame(animate);
    };

    animationId = window.requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationId);
    };
  }, []);

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
      }}
    >
      <Box
        component="canvas"
        ref={canvasRef}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          left: "50%",
          top: "-10%",
          width: 520,
          height: 520,
          transform: "translateX(-50%)",
          borderRadius: "50%",
          backgroundColor: "rgba(10, 10, 10, 0.035)",
          filter: "blur(100px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-5%",
          right: "-5%",
          width: 360,
          height: 360,
          borderRadius: "50%",
          backgroundColor: "rgba(34, 197, 94, 0.06)",
          filter: "blur(90px)",
        }}
      />
    </Box>
  );
}
